import { Redis } from "ioredis";

import { logger } from "../utils/logger";

/**
 * Redis Service for caching and real-time features
 * Handles online status, message caching, and Socket.io adapter
 */
class RedisService {
  private client: Redis | null = null;
  private subscriber: Redis | null = null;
  private publisher: Redis | null = null;
  private isConnected = false;

  /**
   * Initialize Redis connections
   */
  async initialize(): Promise<void> {
    try {
      const redisConfig = {
        host: process.env.REDIS_HOST || "localhost",
        port: Number.parseInt(process.env.REDIS_PORT || "6379", 10),
        password: process.env.REDIS_PASSWORD || undefined,
        retryStrategy: (times: number) => {
          const delay = Math.min(times * 50, 2000);
          return delay;
        },
        maxRetriesPerRequest: 3,
        enableReadyCheck: true,
        lazyConnect: true, // Connect manually
      };

      // Main client for general operations
      this.client = new Redis(redisConfig);

      // Subscriber for pub/sub
      this.subscriber = new Redis(redisConfig);

      // Publisher for pub/sub
      this.publisher = new Redis(redisConfig);

      // Event handlers for main client
      this.client.on("connect", () => {
        logger.info("Redis client connected");
      });

      this.client.on("ready", () => {
        this.isConnected = true;
        logger.info("Redis client ready");
      });

      this.client.on("error", error => {
        // Only log non-connection errors to reduce noise when Redis is unavailable
        const errorCode = (error as any)?.code;
        if (errorCode !== "ECONNREFUSED" && errorCode !== "ENOTFOUND") {
          logger.error("Redis client error:", error);
        }
        this.isConnected = false;
      });

      this.client.on("close", () => {
        // Only log if we were previously connected
        if (this.isConnected) {
          logger.warn("Redis client connection closed");
        }
        this.isConnected = false;
      });

      // Event handlers for subscriber
      this.subscriber.on("connect", () => {
        logger.info("Redis subscriber connected");
      });

      this.subscriber.on("error", error => {
        // Only log non-connection errors to reduce noise when Redis is unavailable
        const errorCode = (error as any)?.code;
        if (errorCode !== "ECONNREFUSED" && errorCode !== "ENOTFOUND") {
          logger.error("Redis subscriber error:", error);
        }
      });

      // Event handlers for publisher
      this.publisher.on("connect", () => {
        logger.info("Redis publisher connected");
      });

      this.publisher.on("error", error => {
        // Only log non-connection errors to reduce noise when Redis is unavailable
        const errorCode = (error as any)?.code;
        if (errorCode !== "ECONNREFUSED" && errorCode !== "ENOTFOUND") {
          logger.error("Redis publisher error:", error);
        }
      });

      // Connect clients (since lazyConnect is true, we need to connect manually)
      await Promise.all([
        this.client.connect().catch(() => {
          // Ignore connection errors, we'll handle them via event handlers
        }),
        this.subscriber.connect().catch(() => {
          // Ignore connection errors, we'll handle them via event handlers
        }),
        this.publisher.connect().catch(() => {
          // Ignore connection errors, we'll handle them via event handlers
        }),
      ]);

      // Wait for connections with timeout (2 seconds for faster failure)
      const CONNECTION_TIMEOUT = 2000;
      try {
        await Promise.race([
          Promise.all([
            new Promise<void>((resolve, reject) => {
              if (!this.client) {
                reject(new Error("Redis client not initialized"));
                return;
              }
              if (this.client.status === "ready") {
                resolve();
                return;
              }
              const readyHandler = () => {
                cleanup();
                resolve();
              };
              const errorHandler = (err: Error) => {
                cleanup();
                reject(err);
              };
              const cleanup = () => {
                this.client?.removeListener("ready", readyHandler);
                this.client?.removeListener("error", errorHandler);
              };
              this.client.once("ready", readyHandler);
              this.client.once("error", errorHandler);
            }),
            new Promise<void>((resolve, reject) => {
              if (!this.subscriber) {
                reject(new Error("Redis subscriber not initialized"));
                return;
              }
              if (this.subscriber.status === "ready") {
                resolve();
                return;
              }
              const readyHandler = () => {
                cleanup();
                resolve();
              };
              const errorHandler = (err: Error) => {
                cleanup();
                reject(err);
              };
              const cleanup = () => {
                this.subscriber?.removeListener("ready", readyHandler);
                this.subscriber?.removeListener("error", errorHandler);
              };
              this.subscriber.once("ready", readyHandler);
              this.subscriber.once("error", errorHandler);
            }),
            new Promise<void>((resolve, reject) => {
              if (!this.publisher) {
                reject(new Error("Redis publisher not initialized"));
                return;
              }
              if (this.publisher.status === "ready") {
                resolve();
                return;
              }
              const readyHandler = () => {
                cleanup();
                resolve();
              };
              const errorHandler = (err: Error) => {
                cleanup();
                reject(err);
              };
              const cleanup = () => {
                this.publisher?.removeListener("ready", readyHandler);
                this.publisher?.removeListener("error", errorHandler);
              };
              this.publisher.once("ready", readyHandler);
              this.publisher.once("error", errorHandler);
            }),
          ]),
          new Promise<void>((_, reject) => {
            setTimeout(() => {
              reject(new Error("Redis connection timeout"));
            }, CONNECTION_TIMEOUT);
          }),
        ]);
        logger.info("Redis service initialized successfully");
      } catch (error) {
        // If Redis connection fails or times out, silently continue without Redis
        // Only log if it's not a simple connection refused (Redis not running)
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        const errorCode = (error as any)?.code;
        if (
          errorCode !== "ECONNREFUSED" &&
          errorCode !== "ENOTFOUND" &&
          !errorMessage.includes("timeout")
        ) {
          logger.warn(
            "Redis connection failed, continuing without Redis:",
            errorMessage,
          );
        }
        this.isConnected = false;
        // Don't throw - allow server to continue without Redis
      }
    } catch (error) {
      // Outer catch for any unexpected errors during initialization
      // Don't log connection refused errors to reduce noise
      const errorCode = (error as any)?.code;
      if (errorCode !== "ECONNREFUSED" && errorCode !== "ENOTFOUND") {
        logger.error("Failed to initialize Redis service:", error);
      }
      this.isConnected = false;
      // Don't throw - allow server to continue without Redis
    }
  }

  /**
   * Get Redis client instances for Socket.io adapter
   */
  getClients(): { pubClient: Redis; subClient: Redis } {
    if (!this.publisher || !this.subscriber) {
      throw new Error("Redis clients not initialized");
    }
    return {
      pubClient: this.publisher,
      subClient: this.subscriber,
    };
  }

  /**
   * Get main Redis client
   */
  getClient(): Redis {
    if (!this.client) {
      throw new Error("Redis client not initialized");
    }
    return this.client;
  }

  /**
   * Check if Redis is connected
   */
  isReady(): boolean {
    return this.isConnected && this.client?.status === "ready";
  }

  /**
   * Set user online status
   */
  async setUserOnline(userId: string, role: string): Promise<void> {
    if (!this.isReady()) return;

    try {
      const key = `user:online:${userId}`;
      const data = JSON.stringify({
        userId,
        role,
        lastSeen: new Date().toISOString(),
      });
      if (!this.client) return;
      await this.client.setex(key, 300, data); // 5 minutes TTL

      // Add to online users set
      await this.client.sadd(`online:${role}`, userId);
    } catch (error) {
      logger.error("Error setting user online:", error);
    }
  }

  /**
   * Set user offline status
   */
  async setUserOffline(userId: string, role: string): Promise<void> {
    if (!this.isReady()) return;

    try {
      if (!this.client) return;
      const key = `user:online:${userId}`;
      await this.client.del(key);

      // Remove from online users set
      await this.client.srem(`online:${role}`, userId);
    } catch (error) {
      logger.error("Error setting user offline:", error);
    }
  }

  /**
   * Check if user is online
   */
  async isUserOnline(userId: string): Promise<boolean> {
    if (!this.isReady()) return false;

    try {
      if (!this.client) return false;
      const key = `user:online:${userId}`;
      const exists = await this.client.exists(key);
      return exists === 1;
    } catch (error) {
      logger.error("Error checking user online status:", error);
      return false;
    }
  }

  /**
   * Get user online status with details
   */
  async getUserOnlineStatus(userId: string): Promise<{
    isOnline: boolean;
    lastSeen?: string;
    role?: string;
  }> {
    if (!this.isReady()) {
      return { isOnline: false };
    }

    try {
      if (!this.client) {
        return { isOnline: false };
      }
      const key = `user:online:${userId}`;
      const data = await this.client.get(key);

      if (!data) {
        return { isOnline: false };
      }

      const parsed = JSON.parse(data);
      return {
        isOnline: true,
        lastSeen: parsed.lastSeen,
        role: parsed.role,
      };
    } catch (error) {
      logger.error("Error getting user online status:", error);
      return { isOnline: false };
    }
  }

  /**
   * Get all online users for a role
   */
  async getOnlineUsers(role: string): Promise<string[]> {
    if (!this.isReady()) return [];

    try {
      if (!this.client) return [];
      const members = await this.client.smembers(`online:${role}`);
      return members;
    } catch (error) {
      logger.error("Error getting online users:", error);
      return [];
    }
  }

  /**
   * Cache message for quick retrieval
   */
  async cacheMessage(
    chatId: string,
    messageId: string,
    messageData: any,
    ttl = 3600,
  ): Promise<void> {
    if (!this.isReady()) return;

    try {
      if (!this.client) return;
      const key = `message:${chatId}:${messageId}`;
      await this.client.setex(key, ttl, JSON.stringify(messageData));
    } catch (error) {
      logger.error("Error caching message:", error);
    }
  }

  /**
   * Get cached message
   */
  async getCachedMessage(
    chatId: string,
    messageId: string,
  ): Promise<Record<string, unknown> | null> {
    if (!this.isReady()) return null;

    try {
      if (!this.client) return null;
      const key = `message:${chatId}:${messageId}`;
      const data = await this.client.get(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      logger.error("Error getting cached message:", error);
      return null;
    }
  }

  /**
   * Cache chat list for user
   */
  async cacheUserChats(
    userId: string,
    role: string,
    chats: any[],
    ttl = 300,
  ): Promise<void> {
    if (!this.isReady()) return;

    try {
      if (!this.client) return;
      const key = `chats:${role}:${userId}`;
      await this.client.setex(key, ttl, JSON.stringify(chats));
    } catch (error) {
      logger.error("Error caching user chats:", error);
    }
  }

  /**
   * Get cached chat list
   */
  async getCachedUserChats(
    userId: string,
    role: string,
  ): Promise<any[] | null> {
    if (!this.isReady()) return null;

    try {
      if (!this.client) return null;
      const key = `chats:${role}:${userId}`;
      const data = await this.client.get(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      logger.error("Error getting cached chats:", error);
      return null;
    }
  }

  /**
   * Invalidate chat cache for user
   */
  async invalidateUserChats(userId: string, role: string): Promise<void> {
    if (!this.isReady()) return;

    try {
      if (!this.client) return;
      const key = `chats:${role}:${userId}`;
      await this.client.del(key);
    } catch (error) {
      logger.error("Error invalidating chat cache:", error);
    }
  }

  /**
   * Cache messages for a chat (recent messages)
   */
  async cacheChatMessages(
    chatId: string,
    messages: any[],
    ttl = 600,
  ): Promise<void> {
    if (!this.isReady()) return;

    try {
      if (!this.client) return;
      const key = `messages:${chatId}`;
      await this.client.setex(key, ttl, JSON.stringify(messages));
    } catch (error) {
      logger.error("Error caching chat messages:", error);
    }
  }

  /**
   * Get cached messages for a chat
   */
  async getCachedChatMessages(chatId: string): Promise<any[] | null> {
    if (!this.isReady()) return null;

    try {
      if (!this.client) return null;
      const key = `messages:${chatId}`;
      const data = await this.client.get(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      logger.error("Error getting cached chat messages:", error);
      return null;
    }
  }

  /**
   * Invalidate chat messages cache
   */
  async invalidateChatMessages(chatId: string): Promise<void> {
    if (!this.isReady()) return;

    try {
      if (!this.client) return;
      const key = `messages:${chatId}`;
      await this.client.del(key);
    } catch (error) {
      logger.error("Error invalidating chat messages cache:", error);
    }
  }

  /**
   * Cache chat details
   */
  async cacheChat(chatId: string, chatData: any, ttl = 1800): Promise<void> {
    if (!this.isReady()) return;

    try {
      if (!this.client) return;
      const key = `chat:${chatId}`;
      await this.client.setex(key, ttl, JSON.stringify(chatData));
    } catch (error) {
      logger.error("Error caching chat:", error);
    }
  }

  /**
   * Get cached chat details
   */
  async getCachedChat(chatId: string): Promise<Record<string, unknown> | null> {
    if (!this.isReady()) return null;

    try {
      if (!this.client) return null;
      const key = `chat:${chatId}`;
      const data = await this.client.get(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      logger.error("Error getting cached chat:", error);
      return null;
    }
  }

  /**
   * Invalidate chat cache
   */
  async invalidateChat(chatId: string): Promise<void> {
    if (!this.isReady()) return;

    try {
      if (!this.client) return;
      const key = `chat:${chatId}`;
      await this.client.del(key);
    } catch (error) {
      logger.error("Error invalidating chat cache:", error);
    }
  }

  /**
   * Increment unread count in cache
   */
  async incrementUnreadCount(
    chatId: string,
    userId: string,
    ttl = 86_400,
  ): Promise<void> {
    if (!this.isReady()) return;

    try {
      if (!this.client) return;
      const key = `unread:${chatId}:${userId}`;
      await this.client.incr(key);
      await this.client.expire(key, ttl);
    } catch (error) {
      logger.error("Error incrementing unread count:", error);
    }
  }

  /**
   * Reset unread count in cache
   */
  async resetUnreadCount(chatId: string, userId: string): Promise<void> {
    if (!this.isReady()) return;

    try {
      if (!this.client) return;
      const key = `unread:${chatId}:${userId}`;
      await this.client.del(key);
    } catch (error) {
      logger.error("Error resetting unread count:", error);
    }
  }

  /**
   * Get unread count from cache
   */
  async getUnreadCount(chatId: string, userId: string): Promise<number> {
    if (!this.isReady()) return 0;

    try {
      if (!this.client) return 0;
      const key = `unread:${chatId}:${userId}`;
      const count = await this.client.get(key);
      return count ? Number.parseInt(count, 10) : 0;
    } catch (error) {
      logger.error("Error getting unread count:", error);
      return 0;
    }
  }

  /**
   * Close all Redis connections
   */
  async close(): Promise<void> {
    try {
      if (this.client) {
        await this.client.quit();
      }
      if (this.subscriber) {
        await this.subscriber.quit();
      }
      if (this.publisher) {
        await this.publisher.quit();
      }
      this.isConnected = false;
      logger.info("Redis connections closed");
    } catch (error) {
      logger.error("Error closing Redis connections:", error);
    }
  }
}

export const redisService = new RedisService();
